import clsx from 'clsx';
import {
  useEffect,
  useRef,
  type ButtonHTMLAttributes,
  type PropsWithChildren,
  type ReactNode,
  type Ref,
} from 'react';
import { X } from 'lucide-react';
import { useToasts } from '../lib/toast.js';

/* Pastel avatar colours — one per person, stable across renders. The palette
   matches the POC's rail screenshots (soft blue/green/peach/pink/violet). */
const AVATAR_TONES = [
  { bg: '#deebff', fg: '#0b57e3' },
  { bg: '#e8f6ef', fg: '#067647' },
  { bg: '#fdeada', fg: '#8a5300' },
  { bg: '#fdeef0', fg: '#b42318' },
  { bg: '#efe9fb', fg: '#5b3fa8' },
  { bg: '#e0f2f5', fg: '#0a6a78' },
];

export function avatarTone(name: string): { bg: string; fg: string } {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return AVATAR_TONES[Math.abs(hash) % AVATAR_TONES.length]!;
}

export function Avatar({
  emoji,
  name,
  size = 'md',
  online,
  ready,
  className,
}: {
  emoji: string;
  name: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  online?: boolean;
  ready?: boolean;
  className?: string;
}) {
  const sizes = {
    xs: 'h-5 w-5 text-[10px]',
    sm: 'h-[26px] w-[26px] text-[10px]',
    md: 'h-[34px] w-[34px] text-xs',
    lg: 'h-14 w-14 text-lg',
  };
  const tone = avatarTone(name);
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <span className={clsx('relative inline-flex shrink-0', className)}>
      <span
        className={clsx('inline-flex items-center justify-center rounded-full font-bold leading-none', sizes[size])}
        style={{ background: emoji ? 'var(--brand-100, #deebff)' : tone.bg, color: emoji ? undefined : tone.fg }}
        title={name}
        aria-label={name}
      >
        {emoji || initials}
      </span>
      {ready ? (
        <span
          className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-ok text-[8px] text-white ring-2 ring-white"
          title={`${name} is ready`}
        >
          ✓
        </span>
      ) : online !== undefined ? (
        <span
          className={clsx(
            'absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-white',
            online ? 'bg-[#12b76a]' : 'bg-black/25',
          )}
          title={online ? `${name} is online` : `${name} is offline`}
        />
      ) : null}
    </span>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-sm text-muted">
      <span
        className="h-4 w-4 animate-spin rounded-full border-2 border-brand-500 border-t-transparent"
        aria-hidden
      />
      {label}
    </span>
  );
}

export function FullPageState({ title, children }: PropsWithChildren<{ title: string }>) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-3 p-6 text-center">
      <h1 className="text-lg font-semibold">{title}</h1>
      <div className="max-w-md text-sm text-muted">{children}</div>
    </div>
  );
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  width = 'md',
}: PropsWithChildren<{
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  footer?: ReactNode;
  width?: 'sm' | 'md' | 'lg';
}>) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Stop the board behind the dialog from scrolling on touch devices.
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  useEffect(() => {
    if (open) ref.current?.focus();
  }, [open]);

  if (!open) return null;

  const widths = { sm: 'max-w-sm', md: '', lg: 'ov-panel-lg' };

  return (
    <div className="overlay">
      <button type="button" className="ov-backdrop" aria-label="Close" onClick={onClose} />
      <div ref={ref} tabIndex={-1} role="dialog" aria-modal="true" className={clsx('ov-panel', widths[width])}>
        <div className="ov-head">
          <h3>{title}</h3>
          <button type="button" onClick={onClose} className="icon-btn" aria-label="Close dialog">
            <X size={18} />
          </button>
        </div>
        <div className="ov-body">{children}</div>
        {footer ? <div className="ov-foot">{footer}</div> : null}
      </div>
    </div>
  );
}

export function IconButton({
  label,
  children,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; ref?: Ref<HTMLButtonElement> }) {
  return (
    <button type="button" title={label} aria-label={label} className={clsx('icon-btn', className)} {...rest}>
      {children}
    </button>
  );
}

export function Toaster() {
  const { toasts, dismiss } = useToasts();

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-[84px] z-[80] flex flex-col items-center gap-2 px-4 lg:bottom-[22px]">
      {toasts.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => dismiss(item.id)}
          className={clsx(
            'pointer-events-auto max-w-md rounded-[10px] px-4 py-2.5 text-left text-[13px] font-semibold text-white shadow-panel animate-pop-in',
            item.tone === 'error' ? 'bg-bad' : 'bg-navy',
          )}
          role="status"
        >
          {item.message}
        </button>
      ))}
    </div>
  );
}

export function EmptyState({ emoji, title, children }: PropsWithChildren<{ emoji: string; title: string }>) {
  return (
    <div className="empty">
      <span className="text-3xl" aria-hidden>
        {emoji}
      </span>
      <strong>{title}</strong>
      <p className="max-w-sm mx-auto">{children}</p>
    </div>
  );
}

/** POC page head: eyebrow + h2 + lede + right-aligned actions. */
export function PageHead({
  eyebrow,
  title,
  lede,
  actions,
}: {
  eyebrow?: string;
  title: string;
  lede?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="ph">
      <div>
        {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
        <h2>{title}</h2>
        {lede ? <p className="lede">{lede}</p> : null}
      </div>
      {actions ? <div className="ph-actions">{actions}</div> : null}
    </div>
  );
}

/** POC stat card: label, big value, sub-note. */
export function Stat({ label, value, note }: { label: string; value: ReactNode; note?: ReactNode }) {
  return (
    <div className="stat">
      <span>{label}</span>
      <strong>{value}</strong>
      {note ? <small>{note}</small> : null}
    </div>
  );
}

export function Notice({ tone = 'info', children }: PropsWithChildren<{ tone?: 'info' | 'neutral' | 'warn' }>) {
  const cls = tone === 'neutral' ? 'notice-neutral' : tone === 'warn' ? 'notice-warn' : 'notice';
  return <div className={cls}>{children}</div>;
}
